import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { CHANNEL_META } from '../../utils/helpers';

const MONTHS    = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const WEEK_DAYS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

export default function Calendar() {
  const { contentPieces, scheduled, schedulePiece, unschedulePiece } = useApp();
  const [calOffset, setCalOffset] = useState(0);
  const [dayPopup,  setDayPopup]  = useState(null);
  const [dragId,    setDragId]    = useState(null);

  const today  = new Date();
  const anchor = new Date(today.getFullYear(), today.getMonth() + calOffset, 1);
  const y = anchor.getFullYear();
  const m = anchor.getMonth();
  const firstDow = (new Date(y, m, 1).getDay() + 6) % 7;
  const lastDate = new Date(y, m + 1, 0).getDate();

  const days = [];
  for (let i = 0; i < firstDow; i++) days.push(null);
  for (let d = 1; d <= lastDate; d++) days.push(new Date(y, m, d));
  while (days.length % 7 !== 0) days.push(null);

  const todayStr = today.toISOString().split('T')[0];

  function getPiecesForDate(dateStr) {
    return (scheduled[dateStr] || []).map(id => contentPieces.find(p => p.id === id)).filter(Boolean);
  }

  const unscheduledPieces = contentPieces.filter(p =>
    !p.scheduled_date && (p.status === 'approved' || p.status === 'draft')
  );
  const publishedPieces = contentPieces.filter(p => p.status === 'published');

  const popupPieces      = dayPopup ? getPiecesForDate(dayPopup.dateStr) : [];
  const popupUnscheduled = contentPieces.filter(p => !p.scheduled_date && p.status === 'approved');

  async function handleDrop(dateStr) {
    if (!dragId) return;
    await schedulePiece(dragId, dateStr).catch(console.error);
    setDragId(null);
  }

  return (
    <div style={{ display:'flex', height:'100%', minHeight:0 }}>

      {/* Main calendar */}
      <div style={{ flex:1, overflowY:'auto', padding:'20px 24px' }}>
        <div className="cal-head">
          <div>
            <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:22, fontWeight:700 }}>
              {MONTHS[m]} {y}
            </div>
            <div style={{ fontSize:11, color:'#9ca3af', marginTop:2 }}>
              Click a day to see content · Drag from library to schedule
            </div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <button className="btn btn-outline btn-sm" onClick={() => setCalOffset(o => o-1)}>‹ Prev</button>
            <button className="btn btn-outline btn-sm" onClick={() => setCalOffset(0)}>Today</button>
            <button className="btn btn-outline btn-sm" onClick={() => setCalOffset(o => o+1)}>Next ›</button>
          </div>
        </div>

        {/* Channel legend */}
        <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:12 }}>
          {Object.entries(CHANNEL_META).map(([k, ch]) => (
            <div key={k} style={{ display:'flex', alignItems:'center', gap:4, fontSize:10, color:'#6b7280' }}>
              <div style={{ width:8, height:8, borderRadius:'50%', background:ch.color }} />
              {ch.label}
            </div>
          ))}
        </div>

        {/* Day headers */}
        <div className="cal-grid" style={{ marginBottom:3 }}>
          {WEEK_DAYS.map(d => <div key={d} className="cal-dh">{d}</div>)}
        </div>

        {/* Calendar grid */}
        <div className="cal-grid">
          {days.map((day, idx) => {
            if (!day) return <div key={`empty-${idx}`} className="cal-cell empty" />;
            const dow      = (day.getDay() + 6) % 7;
            const isWeekend = dow >= 5;
            const dateStr  = day.toISOString().split('T')[0];
            const isToday  = dateStr === todayStr;
            const dayPieces = getPiecesForDate(dateStr);
            return (
              <div key={dateStr}
                className={`cal-cell ${isWeekend ? 'weekend' : ''} ${isToday ? 'today' : ''}`}
                data-date={dateStr}
                onClick={() => setDayPopup({ dateStr })}
                onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('drag-over'); }}
                onDrop={e => { e.currentTarget.classList.remove('drag-over'); handleDrop(dateStr); }}
                onDragLeave={e => e.currentTarget.classList.remove('drag-over')}>
                <div className="cal-num">{day.getDate()}</div>
                {dayPieces.slice(0, 3).map(p => (
                  <div key={p.id} className="cal-pip"
                    style={{ background:CHANNEL_META[p.channel]?.color || '#9ca3af' }}
                    title={p.idea_title || p.channel}>
                    {CHANNEL_META[p.channel]?.emoji || '•'} {(p.idea_title || '').slice(0,18)}
                  </div>
                ))}
                {dayPieces.length > 3 && <div style={{ fontSize:8, color:'#9ca3af' }}>+{dayPieces.length-3} more</div>}
                <div className="cal-drop-hint">Drop here</div>
              </div>
            );
          })}
        </div>

        {/* Published history */}
        {publishedPieces.length > 0 && (
          <div style={{ marginTop:24, paddingTop:20, borderTop:'2px solid var(--border)' }}>
            <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:18, fontWeight:700, marginBottom:12 }}>Published</div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(250px,1fr))', gap:8 }}>
              {publishedPieces.slice(0, 8).map(p => (
                <div key={p.id} style={{ background:'var(--white)', borderRadius:'var(--r)', border:'1px solid var(--border)', padding:11, boxShadow:'var(--sh)' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:6 }}>
                    <div style={{ width:8, height:8, borderRadius:'50%', background:CHANNEL_META[p.channel]?.color||'#9ca3af' }} />
                    <div style={{ fontSize:11, fontWeight:700 }}>{CHANNEL_META[p.channel]?.label||p.channel}</div>
                    <div style={{ fontSize:10, color:'#9ca3af', marginLeft:'auto' }}>{p.scheduled_date||''}</div>
                  </div>
                  <div style={{ fontSize:11.5, color:'#4b5563', lineHeight:1.5, overflow:'hidden', display:'-webkit-box', WebkitLineClamp:3, WebkitBoxOrient:'vertical' }}>
                    {p.content||p.finished_post||''}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Right sidebar: ready to schedule */}
      <div className="cal-lib" style={{ width:240, borderLeft:'1px solid var(--border)', overflowY:'auto', flexShrink:0 }}>
        <div className="cal-lib-head">
          <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:14, fontWeight:700 }}>Ready to Schedule</div>
          <div style={{ fontSize:10, color:'#9ca3af', marginTop:2 }}>Drag onto a day · or click a day and pick</div>
        </div>
        {unscheduledPieces.length === 0 ? (
          <div className="empty-state" style={{ padding:'28px 14px', fontSize:11 }}>
            <div style={{ fontSize:24, marginBottom:8 }}>📚</div>
            No content ready to schedule.<br/>Generate content in the Studio and approve it.
          </div>
        ) : (
          unscheduledPieces.map(p => (
            <div key={p.id} draggable
              onDragStart={() => setDragId(p.id)}
              onDragEnd={() => setDragId(null)}
              style={{ padding:'10px 14px', borderBottom:'1px solid var(--border)', cursor:'grab',
                background: dragId === p.id ? 'var(--fog)' : '' }}
              onMouseOver={e => e.currentTarget.style.background = 'var(--fog)'}
              onMouseOut={e => { if (dragId !== p.id) e.currentTarget.style.background = ''; }}>
              <div style={{ display:'flex', alignItems:'center', gap:7, marginBottom:4 }}>
                <div style={{ width:8, height:8, borderRadius:'50%', background:CHANNEL_META[p.channel]?.color||'#9ca3af', flexShrink:0 }} />
                <div style={{ fontSize:11, fontWeight:700 }}>{CHANNEL_META[p.channel]?.label||p.channel}</div>
              </div>
              <div style={{ fontSize:11, color:'#4b5563', overflow:'hidden', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', lineHeight:1.4 }}>
                {(p.idea_title||'').slice(0,50)} — {(p.content||p.finished_post||'').slice(0,60)}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Day popup */}
      {dayPopup && (
        <>
          <div style={{ position:'fixed', inset:0, zIndex:8999 }} onClick={() => setDayPopup(null)} />
          <div style={{ position:'fixed', top:'50%', left:'50%', transform:'translate(-50%,-50%)',
            background:'var(--white)', borderRadius:'var(--r)', border:'1.5px solid var(--border)',
            boxShadow:'var(--sh2)', width:420, maxHeight:'70vh', overflowY:'auto', zIndex:9000, padding:18 }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
              <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:16, fontWeight:700 }}>{dayPopup.dateStr}</div>
              <button onClick={() => setDayPopup(null)}
                style={{ width:24, height:24, borderRadius:5, border:'1px solid var(--border)', background:'var(--fog)', cursor:'pointer', fontSize:12 }}>✕</button>
            </div>
            {popupPieces.length > 0 && (
              <>
                <div style={{ fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.08em', color:'#9ca3af', marginBottom:7 }}>Scheduled</div>
                {popupPieces.map(p => (
                  <div key={p.id} style={{ display:'flex', alignItems:'center', gap:8, padding:8, borderRadius:8, border:'1px solid var(--border)', marginBottom:5, background:'var(--fog)' }}>
                    <div style={{ width:8, height:8, borderRadius:'50%', background:CHANNEL_META[p.channel]?.color||'#9ca3af', flexShrink:0 }} />
                    <div style={{ flex:1, fontSize:11.5, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.idea_title||p.channel}</div>
                    <button className="btn btn-ghost btn-xs" style={{ color:'var(--rose)' }}
                      onClick={() => { unschedulePiece(p.id, dayPopup.dateStr).catch(console.error); setDayPopup(null); }}>✕</button>
                  </div>
                ))}
              </>
            )}
            {popupUnscheduled.length > 0 ? (
              <>
                <div style={{ fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.08em', color:'var(--caramel)', margin:'10px 0 7px' }}>Add from library</div>
                {popupUnscheduled.slice(0,8).map(p => (
                  <div key={p.id}
                    style={{ display:'flex', alignItems:'center', gap:8, padding:8, borderRadius:8, border:'1px solid var(--border)', marginBottom:5, cursor:'pointer' }}
                    onMouseOver={e => e.currentTarget.style.background = 'var(--fog)'}
                    onMouseOut={e => e.currentTarget.style.background = ''}
                    onClick={() => { schedulePiece(p.id, dayPopup.dateStr).catch(console.error); setDayPopup(null); }}>
                    <div style={{ width:8, height:8, borderRadius:'50%', background:CHANNEL_META[p.channel]?.color||'#9ca3af', flexShrink:0 }} />
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:11.5, fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{(p.idea_title||'').slice(0,40)}</div>
                      <div style={{ fontSize:9.5, color:'#9ca3af' }}>{CHANNEL_META[p.channel]?.label||p.channel} · {(p.content||'').slice(0,40)}…</div>
                    </div>
                    <span style={{ fontSize:10, color:'var(--caramel)', fontWeight:600 }}>+ Add</span>
                  </div>
                ))}
              </>
            ) : (
              <div style={{ fontSize:11.5, color:'#9ca3af', padding:'8px 0', textAlign:'center' }}>
                No approved content available.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
